import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Typography } from "antd";
import { SearchOutlined, CloseOutlined } from "uiCore";
import SbRefreshButton from "uiCore/SbRefreshButton/SbRefreshButton";
import SbButton from "uiCore/SbButton/SbButton";
import { SbSelect, FilterOutlined } from "uiCore";
import SbTag from "uiCore/SbTag/SbTag";
import SbTable from "uiCore/SbTable/SbTable";

import { getJobRequisitionView } from "Api/apiService";
import useAuthStore from "Store/useAuthStore";
import { JD_STATUSES } from "constants/jobs.constants";
import { buildFieldConfigMap } from "Utils/formatting";
import { formatTimestamp, toTimestampMs } from "Utils/timeFormat";

import {
  PageContainerStyled,
  PageHeaderStyled,
  HeaderLeftStyled,
  HeaderRightStyled,
  HeadingRowStyled,
  TableWrapperStyled,
} from "styles/common.styles";
import { SearchWrapperStyled, JobsTableWrapperStyled } from "Pages/JobsLibraryV2/JobsLibraryV2.styles";

const { Title, Text } = Typography;

const SEARCH_FIELDS = ["job_code", "sb_requisition_number", "job_title", "business_unit", "location", "function", "sub_function", "job_level"];

const SbAdminAllJobsView = ({ embedded = false, refreshTrigger = 0 }) => {
  const email = useAuthStore((s) => s.email);
  const orgMetaData = useAuthStore((s) => s.orgMetaData);
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [jobData, setJobData] = useState([]);
  const [pageSearchTerm, setPageSearchTerm] = useState("");
  const [searchOptions, setSearchOptions] = useState([]);

  const fieldMap = useMemo(
    () => buildFieldConfigMap(orgMetaData, 'req_no'),
    [orgMetaData],
  );
  const isSubFunctionVisible = fieldMap?.sub_function?.visible !== false;

  const fetchJobs = async () => {
    setLoading(true);
    try {
      const data = await getJobRequisitionView(email);
      setJobData(data ?? []);
    } catch (err) {
      console.error("Error fetching all jobs:", err);
      setJobData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchJobs();
  }, [refreshTrigger]);

  useEffect(() => {
    if (!jobData.length) { setSearchOptions([]); return; }
    const unique = new Set();
    jobData.forEach((row) =>
      SEARCH_FIELDS.forEach((f) => {
        const v = row[f];
        if (v && typeof v === "string" && v.trim() !== "--") unique.add(v);
      })
    );
    setSearchOptions(Array.from(unique).map((v) => ({ value: v, label: v })));
  }, [jobData]);

  const filteredJobs = useMemo(() => {
    const term = (pageSearchTerm ?? "").trim().toLowerCase();
    if (!term) return jobData;
    return jobData.filter((row) =>
      SEARCH_FIELDS.some((f) => row[f]?.toString().toLowerCase().includes(term))
    );
  }, [jobData, pageSearchTerm]);

  const getFilters = (key) =>
    [...new Set((filteredJobs || []).map((r) => r[key]).filter(Boolean))].map((v) => ({
      text: v,
      value: v,
    }));

  const columns = [
        {
      title: 'Job Code',
      dataIndex: 'job_code',
      filters: getFilters('job_code'),
      onFilter: (value, record) => record.job_code === value,
    },
    {
      title: fieldMap['job_code']?.label,
      dataIndex: 'sb_requisition_number',
      filters: getFilters('sb_requisition_number'),
      onFilter: (value, record) => record.sb_requisition_number === value,
    },
    {
      title: fieldMap['job_title']?.label,
      dataIndex: 'job_title',
      render: (_, record) => record.job_title_for_jd || record.job_title || "",
      filters: getFilters('job_title'),
      onFilter: (value, record) => record.job_title === value,
    },
    {
      title: fieldMap['business_unit']?.label,
      dataIndex: 'business_unit',
      filters: getFilters('business_unit'),
      onFilter: (value, record) => record.business_unit === value,
    },
    {
      title: fieldMap['location']?.label,
      dataIndex: 'location',
      filters: getFilters('location'),
      onFilter: (value, record) => record.location === value,
    },
    {
      title: fieldMap['function']?.label,
      dataIndex: 'function',
      filters: getFilters('function'),
      onFilter: (value, record) => record.function === value,
    },
    ...(isSubFunctionVisible
      ? [
        {
          title: fieldMap['sub_function']?.label,
          dataIndex: 'sub_function',
          filters: getFilters('sub_function'),
          onFilter: (value, record) => record.sub_function === value,
        },
      ]
      : []),
    {
      title: fieldMap['job_level']?.label,
      dataIndex: 'job_level',
      filters: getFilters('job_level'),
      onFilter: (value, record) => record.job_level === value,
    },
    {
      title: 'Last Updated',
      dataIndex: 'last_updated_time_stamp',
      defaultSortOrder: 'descend',
      sorter: (a, b) => toTimestampMs(a.last_updated_time_stamp) - toTimestampMs(b.last_updated_time_stamp),
      render: (ts) => formatTimestamp(ts) || "—",
    },
    {
      title: 'Status',
      dataIndex: 'jd_status',
      filters: [
        { text: 'Published', value: JD_STATUSES.PUBLISHED },
        { text: 'Unpublished', value: JD_STATUSES.UNPUBLISHED },
      ],
      onFilter: (value, record) =>
        value === JD_STATUSES.PUBLISHED
          ? record.jd_status === JD_STATUSES.PUBLISHED
          : record.jd_status !== JD_STATUSES.PUBLISHED,
      render: (status) => (
        <SbTag color={status === JD_STATUSES.PUBLISHED ? 'success' : 'processing'}>
          {status === JD_STATUSES.PUBLISHED ? 'Published' : 'Unpublished'}
        </SbTag>
      ),
    },
  ];

  const getRowClassName = (record) => {
    const isJobApprovalPending =
      record.jd_status === "PENDING_APPROVAL" && record.assigned_approver_email === email;
    const isJobNeedRevision =
      record.jd_status === "NEEDS_REVISION" && record.user_id === email;

    if (isJobApprovalPending) return "jobs-table-content job-approval-pending";
    if (isJobNeedRevision) return "jobs-table-content needs-revision-jd-row";
    if (record.jd_status !== "PUBLISHED") return "jobs-table-content unpublished-jd-row";
    return "jobs-table-content";
  };

  const searchBar = (
    <SearchWrapperStyled>
      <SbSelect
        options={searchOptions}
        value={pageSearchTerm || undefined}
        suffixIcon={
          <SbButton buttonType="primary" size="small" icon={<SearchOutlined />} />
        }
        onChange={(v) => setPageSearchTerm(v ?? "")}
        placeholder="Search by job code, title…"
        allowClear
        showSearch
        notFoundContent="No matching results"
        listHeight={300}
      />
    </SearchWrapperStyled>
  );

  const table = (
    <JobsTableWrapperStyled>
      <SbTable
        columns={columns}
        dataSource={filteredJobs}
        rowKey={(r, idx) => r.sb_unique_id ?? idx}
        loading={loading}
        sticky
        rowClassName={getRowClassName}
        onRow={() => ({ style: { cursor: "default" } })}
        pagination={{
          showTotal: (total) => {
            const hasActiveFilters = total < (filteredJobs?.length ?? 0);
            return (
              <div className="paic-pagination-total">
                <FilterOutlined />
                <span className={hasActiveFilters ? "is-active-text" : "is-empty-text"}>
                  {hasActiveFilters
                    ? `${total} Jobs selected`
                    : "0 Jobs selected. Please select at least one role"}
                </span>
              </div>
            );
          },
        }}
      />
    </JobsTableWrapperStyled>
  );

  if (embedded) {
    return (
      <>
        <PageHeaderStyled>
          <HeaderLeftStyled>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Text type="secondary">View all jobs and their current status</Text>
              <SbRefreshButton onClick={fetchJobs} />
            </div>
          </HeaderLeftStyled>
          <HeaderRightStyled>{searchBar}</HeaderRightStyled>
        </PageHeaderStyled>
        <TableWrapperStyled>{table}</TableWrapperStyled>
      </>
    );
  }

  return (
    <PageContainerStyled>
      <PageHeaderStyled>
        <HeaderLeftStyled>
          <HeadingRowStyled>
            <Title level={3}>All Jobs</Title>
          </HeadingRowStyled>
          <Text type="secondary">View all jobs and their current status</Text>
        </HeaderLeftStyled>

        <HeaderRightStyled>
          <SbButton
            buttonType="action"
            size="small"
            icon={<CloseOutlined />}
            onClick={() => navigate('/profile', { replace: true })}
            title="Back to Manage Profiles"
          />
          {searchBar}
        </HeaderRightStyled>
      </PageHeaderStyled>

      <TableWrapperStyled>{table}</TableWrapperStyled>
    </PageContainerStyled>
  );
};

export default SbAdminAllJobsView;
